import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Price from 'components/price';

import { getNextConfigVersion } from 'utils/project-definition/project-definition-data-access';

import {
  canRemoveProductFromSystemDefinition,
  canReplaceProductInSystemDefinition,
  removeProductFromSystemDefinition,
} from 'utils/project-definition/component';

import { addConfigNote, getNumConfigNotes, setConfigNoteLocation } from 'utils/project-definition/note-utils';

import { isObjectSelectable, isObjectEditable } from 'utils/project-definition/config-item-info';

import ConfigView from './config-view';
import SelectionBorder from './selection-border';
import NoteEditingLayer from './note-editing-layer';
import ErrorList from './error-list/error-list';

const Mode = {
  SELECTION: 'selection',
  NOTES: 'notes',
};

export default class Configurator extends React.Component {
  constructor(props) {
    super(props);

    // Bind methods
    this.renderConfigViewOverlay = this.renderConfigViewOverlay.bind(this);
    this.renderUI = this.renderUI.bind(this);
    this.renderNotesLayer = this.renderNotesLayer.bind(this);

    this.clickBackgroundHandler = this.clickBackgroundHandler.bind(this);
    this.clickComponentHandler = this.clickComponentHandler.bind(this);
    this.clickComponentRemoveHandler = this.clickComponentRemoveHandler.bind(this);
    this.clickComponentEditHandler = this.clickComponentEditHandler.bind(this);
    this.clickSelectionHandler = this.clickSelectionHandler.bind(this);
    this.clickNoteModeHandler = this.clickNoteModeHandler.bind(this);
    this.pressNoteHandler = this.pressNoteHandler.bind(this);
    this.updatedConfigHandler = this.updatedConfigHandler.bind(this);
    this.changedSelectedObjectHandler = this.changedSelectedObjectHandler.bind(this);
    this.draggedNoteHandler = this.draggedNoteHandler.bind(this);
    this.updatedNoteContentHandler = this.updatedNoteContentHandler.bind(this);

    this.updateConfigLayoutHandler = this.updateConfigLayoutHandler.bind(this);

    this.removeItem = this.removeItem.bind(this);

    this.clickRenderNoteOverlayHandler = this.clickRenderNoteOverlayHandler.bind(this);
    this.workspaceRefHandler = this.workspaceRefHandler.bind(this);
    this.changedViewportHandler = this.changedViewportHandler.bind(this);
    this.getConfigViewHandler = this.getConfigViewHandler.bind(this);

    // Set initial state

    this.initNoteModeData();

    this.state = {
      mode: Mode.SELECTION,
      config: props.config,
      configVersion: 0,
      configErrors: null,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { config } = nextProps;
    this.setState({ config });
  }

  // Private methods

  setMode(m) {
    if (this.state.mode === m) {
      return;
    }
    this.setState({ mode: m });
  }

  initNoteModeData() {
    // Initialize methods needed for note mode
    this.noteModeData = {
      onDragNote: () => {
        const { config } = this.state;
        this.setState({ config });
      },
      onAddedNote: (note) => {
        const { configView } = this;
        if (configView) {
          configView.closeOpenedNote();
          configView.setNoteOpened(note, true);
        }
        const { config } = this.state;
        this.setState({ config }, this.notifyUpdateConfig);
      },
      onClickNote: (note) => {
        const { configView } = this;
        if (!configView) {
          return;
        }
        configView.toggleNoteOpened(note, true);
      },
      transformPointerCoords: (xcoord, ycoord, output) => {
        let x = xcoord;
        let y = ycoord;

        const pt = output;

        const workSpace = this.workspaceRef;

        const workSpaceBounds = workSpace.getBoundingClientRect();
        const viewport = this.viewport;
        const zoom = viewport ? viewport.calculatedZoom || 1 : 1;
        const invZoom = zoom ? 1 / zoom : 1;

        x -= workSpaceBounds.left + viewport.absX;
        y -= workSpaceBounds.top + viewport.absY;
        x *= invZoom;
        y *= invZoom;

        pt.x = x;
        pt.y = y;
      },
      onPress: () => {
        if (this.configView) {
          // Don't add a new note when another note's info bubble is shown
          // -> Very annoying if you just want to close the info bubble
          if (this.configView.getOpenedNote()) {
            this.configView.closeOpenedNote();
            return false;
          }
        }
        return true;
      },
      onCreatedNote: (note) => {
        if (this.configView) {
          this.configView.editInfoBubble();
          const { onCreatedNote } = this.props;
          if (onCreatedNote) {
            onCreatedNote(note);
          }
        }
      },
      onUpdateNote: (note) => {
        if (this.configView) {
          const { onUpdateNote } = this.props;
          if (onUpdateNote) {
            onUpdateNote(note);
          }
        }
      },
      onDeleteNote: (note) => {
        if (this.configView) {
          const { onDeleteNote } = this.props;
          if (onDeleteNote) {
            onDeleteNote(note);
          }
        }
      },
    };
  }

  getConfigObject() {
    return this.state ? this.state.config : null;
  }

  getConfigVersion() {
    return this.state ? this.state.configVersion : null;
  }

  getUpdateConfigVersion() {
    const { configVersion } = this.state;
    return getNextConfigVersion(configVersion);
  }

  updateConfigVersion() {
    this.setState({ configVersion: this.getUpdateConfigVersion() });
  }

  getSelectedObject() {
    return this.state ? this.state.selectedItem || null : null;
  }

  deselect() {
    this.setSelectedObject(null);
  }

  setSelectedObject(object, imageX = 0, imageY = 0, imageWidth = 0, imageHeight = 0) {
    if (object === this.getSelectedObject()) {
      return;
    }

    let o = object;

    const selectable = isObjectSelectable(o);

    const selectionData = selectable ? {} : null;

    if (!selectable) {
      o = null;
    }

    if (selectionData) {
      selectionData.replaceable = this.isObjectReplaceable(object);
    }

    const { onSetSelectedObject } = this.props;
    if (onSetSelectedObject) {
      onSetSelectedObject(o);
    }

    this.setState(
      {
        selectionData,
        selectionX: imageX,
        selectionY: imageY,
        selectionWidth: imageWidth,
        selectionHeight: imageHeight,
        selectedItem: o,
      },
      this.changedSelectedObjectHandler,
    );
  }

  notifyUpdateConfig() {
    const { onUpdateConfig } = this.props;
    if (onUpdateConfig) {
      onUpdateConfig(this.getConfigObject(), this.getConfigVersion());
    }
  }

  willNotifyConfigUpdates() {
    return !!this.props.onUpdateConfig;
  }

  /* Event listeners */

  updateConfigLayoutHandler(layout, config, errors) {
    const { selectedItem } = this.state;
    let found = false;
    if (layout && selectedItem) {
      // Search for the current selection to be in the new config layout
      const selectedData = selectedItem.data;
      const selectedId = `${selectedData.id}`;
      const { elements } = layout;
      const numElements = elements ? elements.length : 0;

      for (let i = 0; !found && i < numElements; i += 1) {
        const element = elements[i];
        const elementData = element ? element.data : null;
        const elementId = elementData ? `${elementData.id}` : null;
        if (elementId === selectedId) {
          found = true;
          break;
        }
      }
    }
    if (!found) {
      // The previously selected product is not in the new layout
      // -> Clear selection in state
      this.setState({
        selectionX: 0,
        selectionY: 0,
        selectionWidth: 0,
        selectionHeight: 0,
        selectedItem: null,
      });
    }

    this.setState({ configErrors: errors });

    const { onUpdateLayout } = this.props;
    if (onUpdateLayout) {
      onUpdateLayout(layout, config, errors, this);
    }
  }

  updatedNoteContentHandler(note) {
    this.updatedNoteHandler(note);
  }

  updatedNoteHandler(note) {
    this.notifyUpdateNote(note);
    this.notifyUpdateConfig();
  }

  draggedNoteHandler(note) {
    this.notifyUpdateNote(note);
  }

  changedSelectedObjectHandler() {
    const { onChangedSelectedObject } = this.props;
    if (onChangedSelectedObject) {
      onChangedSelectedObject(this.getSelectedObject());
    }
  }

  updatedConfigHandler() {
    this.notifyUpdateConfig();
  }

  getConfigViewHandler(ref) {
    this.configView = ref;
  }

  workspaceRefHandler(ref) {
    this.workspaceRef = ref;
  }

  changedViewportHandler(x, y, zoom, absX, absY, calculatedZoom) {
    const viewport = this.viewport || {};
    this.viewport = viewport;
    viewport.x = x;
    viewport.y = y;
    viewport.zoom = zoom;
    viewport.absX = absX;
    viewport.absY = absY;
    viewport.calculatedZoom = calculatedZoom;
  }

  pressNoteHandler(note, noteIndex, comp) {
    const panningAllowed = comp.isPanningAllowed();

    comp.setPanningAllowed(false);

    const container = comp.getContainerElement();
    const bounds = container.getBoundingClientRect();
    const zoom = comp.getCalcZoom();

    const absViewportX = comp.absoluteViewportX;
    const absViewportY = comp.absoluteViewportY;

    const factor = 1 / zoom;

    const pressX = note.x;
    const pressY = note.y;

    const dragHandler = (dragEvt) => {
      let touches = dragEvt.touches;
      if (!touches || !touches.length) {
        touches = dragEvt.changedTouches;
      }
      let clientX = 0;
      let clientY = 0;
      if (touches && touches.length > 0) {
        const touch = touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = dragEvt.clientX;
        clientY = dragEvt.clientY;
      }
      const dragX = clientX - bounds.left - absViewportX;
      const dragY = clientY - bounds.top - absViewportY;

      setConfigNoteLocation(note, dragX * factor, dragY * factor);

      this.setState({ config: this.state.config });
    };

    const releaseHandler = () => {
      const { x, y } = note;

      // Check if note item has moved by calculating the distance
      // between the position when pressed and the position when released
      // -> trigger update notification if the note has moved
      const distanceX = x - pressX;
      const distanceY = y - pressY;
      const distanceSquared = distanceX * distanceX + distanceY * distanceY; // drag distance squared

      comp.setPanningAllowed(panningAllowed);
      window.removeEventListener('mousemove', dragHandler);
      window.removeEventListener('touchmove', dragHandler);
      window.removeEventListener('mouseup', releaseHandler);
      window.removeEventListener('touchend', releaseHandler);
      if (distanceSquared > 0) {
        this.notifyUpdateNote(note);
      }
      this.setState({ config: this.state.config }, this.notifyUpdateConfig);
    };
    window.addEventListener('mousemove', dragHandler);
    window.addEventListener('touchmove', dragHandler);
    window.addEventListener('mouseup', releaseHandler);
    window.addEventListener('touchend', releaseHandler);
  }

  clickNoteModeHandler() {
    const newMode = this.state.mode === Mode.NOTES ? null : Mode.NOTES;
    this.setMode(newMode);
  }

  clickComponentEditHandler(object) {
    const { onEditItem, configProducts } = this.props;
    if (onEditItem) {
      onEditItem(object, this.getConfigObject(), configProducts);
    }
  }

  removeItem(object) {
    const config = this.getConfigObject();

    // TODO: move to utils
    let succeed = false;
    const { name } = object;
    succeed = removeProductFromSystemDefinition(name, config, this.props.configProducts) || succeed;
    let selectedItem = this.state ? this.state.selectedItem || null : null;
    if (succeed !== false) {
      let changedSelection = false;
      if (selectedItem === object) {
        selectedItem = null;
        changedSelection = true;
      }

      const configVersion = this.getUpdateConfigVersion();
      if (changedSelection) {
        const { onSetSelectedObject } = this.props;
        onSetSelectedObject(selectedItem);
      }

      this.setState({ selectedItem, config, configVersion }, this.notifyUpdateConfig);
    }
  }

  clickComponentRemoveHandler(object) {
    if (!object) {
      return;
    }
    const { onRemoveItem } = this.props;
    if (onRemoveItem) {
      const succeed = onRemoveItem(object, this);
      if (succeed !== false) {
        this.removeItem(object);
      }
    } else {
      this.removeItem(object);
    }
  }

  clickSelectionHandler() {
    this.setSelectedObject(null);
  }

  clickAddNote(evt, pressEvt, comp) {
    const config = this.state.config;
    if (!config) {
      return;
    }
    const tgt = pressEvt.currentTarget || pressEvt.target;

    const zoom = comp && comp.getCalcZoom ? comp.getCalcZoom() : 1;
    const factor = 1 / zoom;

    const rect = tgt.getBoundingClientRect();
    const x = Math.floor((evt.clientX - rect.left) * factor);
    const y = Math.floor((evt.clientY - rect.top) * factor);

    this.addNewNote(x, y);
  }

  clickBackgroundHandler(evt, pressEvt, comp) {
    this.setSelectedObject(null);

    if (this.state.mode === Mode.NOTES) {
      this.clickAddNote(evt, pressEvt, comp);
    }
  }

  clickComponentHandler(object, comp) {
    if (object === this.getSelectedObject()) {
      this.setSelectedObject(null);
      return;
    }
    // Get the component's rectangle to draw the selection rectangle
    const rect = comp.getRectangle();
    this.setSelectedObject(object, rect.x, rect.y, rect.width, rect.height);
  }

  /* Methods */

  getSystemPrice() {
    const { price } = this.props; // Updated price
    if (typeof price !== 'undefined') {
      return price;
    }
    const { config } = this.state;
    if (!config) {
      return null;
    }
    const { attributes } = config;
    if (!attributes) {
      return null;
    }
    return attributes.price;
  }

  getSystemStatus() {
    const { config } = this.state;
    if (!config) {
      return null;
    }
    const { attributes } = config;
    if (!attributes) {
      return null;
    }
    return attributes.status;
  }

  addNewNote(x, y) {
    const { config } = this.state;
    addConfigNote(config, { x, y, content: '<content>' });
    this.setState({ config }, this.notifyUpdateConfig);
  }

  notifyUpdateNote(note) {
    const { onUpdateNote } = this.props;
    if (onUpdateNote) {
      onUpdateNote(note);
    }
  }

  isObjectEditable(object) {
    return isObjectEditable(object) && !this.props.readOnly;
  }

  isObjectRemovable(object) {
    if (!object) {
      return false;
    }
    const { name, productInfo } = object;
    const { config, configProducts, readOnly } = this.props;

    return !readOnly && canRemoveProductFromSystemDefinition(name, config, configProducts, productInfo);
  }
  /**
   * Checks if there is at least one object in the available product list besides the selected product itself
   * */
  isObjectReplaceable(object) {
    if (!object) {
      return false;
    }
    const { name, productInfo } = object;
    const { config, configProducts } = this.props;
    return canReplaceProductInSystemDefinition(name, config, configProducts, productInfo);
  }
  /* Render */

  renderConfigViewOverlay(x, y, zoom) {
    return this.renderSelection(x, y, zoom);
  }

  renderSelection(x, y, zoom) {
    const selItem = this.getSelectedObject();
    if (!selItem) {
      return null;
    }
    const { selectionX, selectionY, selectionWidth, selectionHeight } = this.state;
    if (!selectionWidth || !selectionHeight) {
      return null;
    }

    const replaceable = this.state.selectionData ? this.state.selectionData.replaceable : false;
    const editable = this.isObjectEditable(selItem) && replaceable;
    const removable = this.isObjectRemovable(selItem);

    let resX = selectionX * zoom + x;
    let resY = selectionY * zoom + y;
    let resW = selectionWidth * zoom;
    let resH = selectionHeight * zoom;

    const hMargin = 20;
    const vMargin = 10;
    const doubleHMargin = hMargin * 2;
    const doubleVMargin = vMargin * 2;
    resW += doubleHMargin;
    resH += doubleVMargin;
    resX -= hMargin;
    resY -= vMargin;

    // Make sure the selection box doesn't get smaller than 50 pix
    // and the 'replace' and 'remove' buttons don't overlap
    const minSelectionWidth = 50;
    if (resW < minSelectionWidth) {
      const diff = minSelectionWidth - resW;
      resW = minSelectionWidth;
      resX -= diff * 0.5;
    }

    let clickEditHandler = null;
    let clickRemoveHandler = null;

    if (editable) {
      clickEditHandler = this.clickComponentEditHandler;
    }
    if (removable) {
      clickRemoveHandler = this.clickComponentRemoveHandler;
    }

    const clickSelectionHandler = this.clickSelectionHandler;

    return (
      <SelectionBorder
        data={selItem}
        editable={editable}
        removable={removable}
        x={resX}
        y={resY}
        width={resW}
        height={resH}
        onClickSelection={clickSelectionHandler}
        onClickEdit={clickEditHandler}
        onClickRemove={clickRemoveHandler}
      />
    ); //
  }

  renderZoomButton(icon, handler) {
    const iconClass = `icon u-center ${icon || ''} icon--md`;

    return (
      <button className="button button-bg-white button-circle button--small" onClick={handler}>
        <span className={iconClass} />
      </button>
    );
  }

  renderOptionButton(icon, color, handler) {
    const iconClass = `icon u-center ${icon || ''} icon--md`;
    const colorClass = `button button-bg-${color} button-circle`;

    return (
      <button className={colorClass} onClick={handler}>
        <span className={iconClass} />
      </button>
    );
  }

  renderNoteButton() {
    const { disabled } = this.props;
    const isNoteMode = this.state.mode === Mode.NOTES;
    const color = isNoteMode ? 'yellow' : 'white';
    const noteButton = this.renderOptionButton('icon-edit-notes', color, disabled ? null : this.clickNoteModeHandler);

    const numConfigNotes = getNumConfigNotes(this.getConfigObject());

    const infoClassName = classnames('note-button-info', { opened: isNoteMode });

    // (Tap anywhere on the screen to add a new comment)
    const message = <FormattedMessage id="configurator.commentMode" />;

    const info = (
      <div className={infoClassName}>
        <div className="note-button-info__content">{message}</div>
      </div>
    );

    const count = numConfigNotes > 0 ? <div className="note-button-info__count">{numConfigNotes}</div> : null;

    return (
      <div>
        {info}
        {noteButton}
        {count}
      </div>
    );
  }

  renderPrice() {
    if (!this.state.config || this.props.showPrices < 1) {
      return null;
    }
    return this.renderPriceValue(this.getSystemPrice());
  }

  renderPriceValue(price) {
    return (
      <div className="configurator__price">
        <Price minimal value={price} vatExcluded />
      </div>
    );
  }
  renderStatusIndicator() {
    const status = this.getSystemStatus();
    if (!status) {
      return null;
    }
    return (
      <div className={`label label--status-${status} configurator__status`}>
        <FormattedMessage id={`general.status.${status}`} />
      </div>
    );
  }

  renderErrors() {
    const { configErrors } = this.state;

    return <ErrorList errors={configErrors} />;
  }

  renderUI(comp) {
    const style = null;

    let addButton = null;
    let undoButton = null;

    let { onClickAddProduct, onClickUndo } = this.props;
    const { disabled, onRenderFooter } = this.props;

    if (disabled) {
      onClickAddProduct = null;
      onClickUndo = null;
    }

    if (onClickAddProduct) {
      addButton = this.renderOptionButton('icon-plus', 'yellow', onClickAddProduct);
    }
    if (onClickUndo) {
      undoButton = this.renderOptionButton('icon-undo', 'white', onClickUndo);
    }

    const noteButton = this.renderNoteButton();

    const footer = onRenderFooter ? onRenderFooter() : null;

    return (
      <div className="configurator__ui-container" style={style}>
        {this.renderErrors()}
        <div className="configurator__ui-container--top-left configurator__ui-container--vertical" style={style}>
          {addButton}
          {undoButton}
        </div>
        <div className="configurator__ui-container--top-right configurator__ui-container--vertical" style={style}>
          {noteButton}
        </div>
        <div className="configurator__ui-container--bottom-left configurator__ui-container--horizontal" style={style}>
          {this.renderZoomButton('icon-zoom-in', comp.zoomIn)}
          {this.renderZoomButton('icon-zoom-out', comp.zoomOut)}
          {this.renderZoomButton('icon-actual-size', comp.zoomToFit)}
          {footer}
        </div>

        <div className="configurator__ui-container--bottom-right configurator__ui-container--horizontal" style={style}>
          {/* this.renderStatusIndicator() */}
          {this.renderPrice()}
        </div>
      </div>
    );
  }

  // Click events on this overlay will add new notes
  renderNotesLayer(notesContainer, noteElements, x, y, zoom, comp) {
    const width = comp.containerWidth;
    const height = comp.containerHeight;

    return (
      <div className="config-note__container">
        {this.renderNoteAddOverlay(0, 0, width, height)}
        {notesContainer}
      </div>
    );
  }

  renderNoteAddOverlay(left, top, width, height) {
    if (this.state.mode !== Mode.NOTES || this.props.disabled) {
      return null;
    }
    const onDraggedNote = this.draggedNoteHandler;

    const config = this.getConfigObject();

    const noteLayerStyle = {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };

    const { noteModeData } = this;

    const {
      onAddedNote,
      onDragNote,
      onClickNote,

      onCreatedNote,

      onPress,
      transformPointerCoords,
    } = noteModeData;

    return (
      <NoteEditingLayer
        style={noteLayerStyle}
        config={config}
        onAddedNote={onAddedNote}
        onDragNote={onDragNote}
        onDraggedNote={onDraggedNote}
        onClickNote={onClickNote}
        onCreatedNote={onCreatedNote}
        onPress={onPress}
        transformPointerCoords={transformPointerCoords}
      />
    );
    //
  }

  clickRenderNoteOverlayHandler(evt) {
    let x = evt.clientX;
    let y = evt.clientY;
    const workSpace = this.workspaceRef;

    const workSpaceBounds = workSpace.getBoundingClientRect();
    const viewport = this.viewport;
    const zoom = viewport ? viewport.calculatedZoom || 1 : 1;
    const invZoom = zoom ? 1 / zoom : 1;

    x -= workSpaceBounds.left;
    y -= workSpaceBounds.top;
    x *= invZoom;
    y *= invZoom;

    this.addNewNote(x, y);
  }

  getInternalConfigVersion() {
    const configVersionProp = this.props.configVersion;
    const configVersionState = this.state.configVersion;
    return `${configVersionProp}_${configVersionState}`;
  }

  render() {
    let pressNoteHandler = null;
    if (this.state.mode === Mode.NOTES) {
      pressNoteHandler = this.pressNoteHandler;
    }

    const { configProducts, onLoaded, disabled, onDeleteNote, className: customClassName, originalSystem } = this.props;
    const className = classnames('configurator', customClassName);

    const onUpdateLayout = this.updateConfigLayoutHandler;

    const { readOnly } = this.props;

    return (
      <div className={className}>
        <ConfigView
          configVersion={this.getInternalConfigVersion()}
          config={this.getConfigObject()}
          configProducts={configProducts}
          disabled={disabled}
          readOnly={readOnly}
          onWorkspaceRef={this.workspaceRefHandler}
          onChangedViewPort={this.changedViewportHandler}
          showGrid
          showFloor
          showNotes
          notesEditable
          notesRemovable
          zoomable
          pannable
          onRenderNotesLayer={this.renderNotesLayer}
          onRenderOverlay={this.renderConfigViewOverlay}
          onRenderUI={this.renderUI}
          onClickComponent={this.clickComponentHandler}
          onClickBackground={this.clickBackgroundHandler}
          onPressNote={pressNoteHandler}
          onUpdateNoteContent={this.updatedNoteContentHandler}
          onDraggedNote={this.draggedNoteHandler}
          onRemovedNote={onDeleteNote}
          onUpdateConfig={this.updatedConfigHandler}
          ref={this.getConfigViewHandler}
          onLoaded={onLoaded}
          onUpdateLayout={onUpdateLayout}
          originalSystem={originalSystem}
        />
      </div>
    );
  }
}

Configurator.defaultProps = {
  disabled: false,
};

Configurator.propTypes = {
  disabled: PropTypes.bool,
  className: PropTypes.string,

  configVersion: PropTypes.any, // change version to trigger config changes
  config: PropTypes.any,
  configProducts: PropTypes.any,

  readOnly: PropTypes.bool,

  price: PropTypes.number,

  onUpdateConfig: PropTypes.func,

  onEditItem: PropTypes.func,
  onRemoveItem: PropTypes.func,

  onClickAddProduct: PropTypes.func,
  onClickUndo: PropTypes.func,

  onSetSelectedObject: PropTypes.func,
  onChangedSelectedObject: PropTypes.func,

  onCreatedNote: PropTypes.func,
  onUpdateNote: PropTypes.func,
  onDeleteNote: PropTypes.func,

  onRenderFooter: PropTypes.func,
  onLoaded: PropTypes.func,
  onUpdateLayout: PropTypes.func,

  originalSystem: PropTypes.any,

  showPrices: PropTypes.number,
};



// WEBPACK FOOTER //
// ./js/components/configuration/configurator.jsx